/**
 * GradeCalculator Component
 * Real-time grade calculation preview based on moisture and purity values.
 *
 * Displays:
 * - Calculated grade badge
 * - Threshold hints (checkmarks for met thresholds)
 * - Price multiplier information
 *
 * @description
 * This component calculates and displays the expected QC grade in real-time
 * as users adjust moisture and purity values in the QC verification form.
 *
 * Grade Calculation Logic:
 * - Grade A: moisture <= 30%, purity >= 90%
 * - Grade B: moisture <= 40%, purity >= 80%
 * - Grade C: moisture <= 50%, purity >= 70%
 * - Grade D: else
 *
 * @author SAUBHAGYA Development Team
 * @since Story 3-4
 */

import { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { QCGradeBadge } from '../badges/QCGradeBadge';

interface GradeCalculatorProps {
  /** Moisture percentage (0-100) */
  moisturePercent: number;
  /** Purity percentage (0-100) */
  purityPercent: number;
  /** Additional CSS classes */
  className?: string;
}

interface GradeThreshold {
  grade: 'A' | 'B' | 'C' | 'D';
  moistureMax: number;
  purityMin: number;
  priceMultiplier: number;
  gradeName: string;
}

// Grade thresholds (should match backend mfg_qc_grade table)
const GRADE_THRESHOLDS: GradeThreshold[] = [
  { grade: 'A', moistureMax: 30, purityMin: 90, priceMultiplier: 1.2, gradeName: 'Premium' },
  { grade: 'B', moistureMax: 40, purityMin: 80, priceMultiplier: 1.0, gradeName: 'Standard' },
  { grade: 'C', moistureMax: 50, purityMin: 70, priceMultiplier: 0.8, gradeName: 'Basic' },
  { grade: 'D', moistureMax: 100, purityMin: 0, priceMultiplier: 0.6, gradeName: 'Poor' },
];

/**
 * Calculate grade based on moisture and purity percentages
 */
function calculateGrade(
  moisturePercent: number,
  purityPercent: number
): GradeThreshold {
  for (const threshold of GRADE_THRESHOLDS) {
    if (
      moisturePercent <= threshold.moistureMax &&
      purityPercent >= threshold.purityMin
    ) {
      return threshold;
    }
  }
  // Default to Grade D
  return GRADE_THRESHOLDS[3];
}

export function GradeCalculator({
  moisturePercent,
  purityPercent,
  className,
}: GradeCalculatorProps) {
  const calculatedGrade = useMemo(
    () => calculateGrade(moisturePercent, purityPercent),
    [moisturePercent, purityPercent]
  );

  // Check which thresholds are met
  const moistureChecks = useMemo(() => {
    return GRADE_THRESHOLDS.slice(0, 3).map((t) => ({
      grade: t.grade,
      maxValue: t.moistureMax,
      met: moisturePercent <= t.moistureMax,
    }));
  }, [moisturePercent]);

  const purityChecks = useMemo(() => {
    return GRADE_THRESHOLDS.slice(0, 3).map((t) => ({
      grade: t.grade,
      minValue: t.purityMin,
      met: purityPercent >= t.purityMin,
    }));
  }, [purityPercent]);

  return (
    <div
      className={cn(
        'rounded-lg border bg-muted/50 p-4 space-y-4',
        className
      )}
    >
      {/* Grade Result */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          Calculated Grade
        </span>
        <QCGradeBadge grade={calculatedGrade.grade} showLabel size="lg" />
      </div>

      {/* Price Multiplier */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Price Multiplier</span>
        <span className="font-semibold">
          {calculatedGrade.priceMultiplier.toFixed(2)}x
        </span>
      </div>

      {/* Threshold Hints */}
      <div className="space-y-3 pt-2 border-t">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Threshold Hints
        </p>

        {/* Moisture Thresholds */}
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">Moisture (lower is better)</p>
          <div className="flex flex-wrap gap-2">
            {moistureChecks.map((check) => (
              <div
                key={check.grade}
                className={cn(
                  'flex items-center gap-1 px-2 py-0.5 rounded text-xs',
                  check.met
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                )}
              >
                {check.met ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <X className="h-3 w-3" />
                )}
                <span>
                  Grade {check.grade}: &le;{check.maxValue}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Purity Thresholds */}
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">Purity (higher is better)</p>
          <div className="flex flex-wrap gap-2">
            {purityChecks.map((check) => (
              <div
                key={check.grade}
                className={cn(
                  'flex items-center gap-1 px-2 py-0.5 rounded text-xs',
                  check.met
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                )}
              >
                {check.met ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <X className="h-3 w-3" />
                )}
                <span>
                  Grade {check.grade}: &ge;{check.minValue}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GradeCalculator;
